/**
 * Loads independently configured Voyado recommendation sections.
 * Compatible sections are combined into Product, Landing, or Cart Page requests.
 */
(function initializeVoyadoRecommendationsSection(window, document) {
    'use strict';

    if (customElements.get('voyado-recommendations-section')) return;

    const PRODUCT_KEY_PARAMETER = 'voyadoProductKey';
    const PRODUCT_CONTEXT_ALGORITHMS = new Set([
        'ALTERNATIVES',
        'MORE_FROM_SERIES',
        'STYLE_WITH',
        'UPSELL',
    ]);

    class VoyadoRecommendationsSection extends HTMLElement {
        connectedCallback() {
            if (this.initialized) return;
            this.initialized = true;
            this.abortController = new AbortController();

            const observer = new IntersectionObserver((entries) => {
                if (!entries[0]?.isIntersecting) return;
                observer.disconnect();
                this.load();
            }, { rootMargin: '0px 0px 200px 0px' });

            this.observer = observer;
            observer.observe(this);
        }

        disconnectedCallback() {
            this.observer?.disconnect();
            this.abortController?.abort();
        }

        get config() {
            if (this.parsedConfig) return this.parsedConfig;

            try {
                this.parsedConfig = JSON.parse(
                    this.querySelector('[data-voyado-config]')?.textContent || '{}'
                );
            } catch (error) {
                this.parsedConfig = {};
                console.warn('[Voyado recommendations] Invalid section configuration', error);
            }

            return this.parsedConfig;
        }

        get productKeys() {
            const linkedProductKey = new URL(window.location.href)
                .searchParams.get(PRODUCT_KEY_PARAMETER);

            return [
                this.config.productKeyOverride,
                linkedProductKey,
                this.config.productKey,
            ]
                .map((key) => String(key || '').trim())
                .filter((key, index, keys) => key && keys.indexOf(key) === index);
        }

        get cartKeys() {
            return Array.isArray(this.config.cartKeys)
                ? this.config.cartKeys
                    .map((key) => String(key || '').trim())
                    .filter((key, index, keys) => key && keys.indexOf(key) === index)
                : [];
        }

        get requestType() {
            const configuredType = String(this.config.requestType || 'auto').toLowerCase();
            if (configuredType !== 'auto') return configuredType;

            const algorithm = String(
                this.config.recommendationList?.algorithm || 'TOP_PRODUCTS'
            ).toUpperCase();

            if (algorithm === 'CART') return 'cart';
            if (PRODUCT_CONTEXT_ALGORITHMS.has(algorithm)) return 'product';
            if (
                algorithm === 'RECENTLY_VIEWED' &&
                this.config.templateName === 'product' &&
                this.productKeys.length
            ) {
                return 'product';
            }

            return 'landing';
        }

        get requestSignature() {
            return JSON.stringify({
                requestType: this.requestType,
                productKeys: this.productKeys,
                pageReference: String(this.config.pageReference || '').trim(),
                cartKeys: this.cartKeys,
            });
        }

        requestGroup() {
            return [...document.querySelectorAll('voyado-recommendations-section')]
                .filter((section) => (
                    !section.requestStarted &&
                    section.requestSignature === this.requestSignature
                ))
                .slice(0, 10);
        }

        recommendationList() {
            const configuredList = this.config.recommendationList || {};
            const list = {
                ...configuredList,
                params: { ...(configuredList.params || {}) },
            };
            const id = String(list.id || '').trim();
            const algorithm = String(list.algorithm || 'TOP_PRODUCTS').toUpperCase();

            if (!/^[A-Za-z0-9_-]{1,20}$/.test(id)) {
                throw new Error(
                    'List ID must contain 1–20 letters, numbers, underscores, or hyphens.'
                );
            }

            const duplicateId = [...document.querySelectorAll(
                'voyado-recommendations-section'
            )].some((section) => (
                section !== this &&
                String(section.config?.recommendationList?.id || '').trim() === id
            ));
            if (duplicateId) {
                throw new Error(`List ID "${id}" is used more than once on this page.`);
            }

            list.id = id;
            list.algorithm = algorithm;
            if (!String(list.label || '').trim()) delete list.label;
            if (!String(list.productRules || '').trim()) delete list.productRules;
            if (!String(list.showMoreLink || '').trim()) delete list.showMoreLink;

            if (PRODUCT_CONTEXT_ALGORITHMS.has(algorithm) && this.requestType !== 'product') {
                if (!this.productKeys.length) {
                    throw new Error(`${algorithm} requires a product key.`);
                }
                list.params.productKey = this.productKeys[0];
            }

            if (algorithm === 'CART' && this.requestType !== 'cart') {
                if (!this.cartKeys.length) throw new Error('CART requires products in the cart.');
                list.params.cart = this.cartKeys;
            }

            if (algorithm === 'MORE_FROM_SERIES' && this.requestType !== 'product') {
                throw new Error('MORE_FROM_SERIES must use the Product Page request context.');
            }

            return list;
        }

        async request(sections) {
            const client = window.VOYADO_ELEVATE?.createClient?.();
            if (!client) throw new Error('Voyado Elevate is not enabled.');
            const recommendationLists = sections.map((section) => (
                section.recommendationList()
            ));
            const options = { signal: this.abortController.signal };

            if (this.requestType === 'landing') {
                const pageReference = String(this.config.pageReference || '').trim();
                if (!pageReference) throw new Error('A landing page reference is required.');

                return client.landingPage(
                    { pageReference },
                    {
                        navigation: { include: false },
                        primaryList: { include: false },
                        recommendationLists,
                    },
                    options
                );
            }

            if (this.requestType === 'cart') {
                if (!this.cartKeys.length) throw new Error('The cart has no product keys.');

                return client.cartPage(
                    { cart: this.cartKeys },
                    {
                        cart: { include: false },
                        recommendationLists,
                    },
                    options
                );
            }

            if (this.requestType !== 'product') {
                throw new Error(`Unsupported request context: ${this.requestType}`);
            }
            if (!this.productKeys.length) throw new Error('A Voyado product key is required.');

            let lastError;
            for (const productKey of this.productKeys) {
                try {
                    return await client.productPage(
                        { productKey },
                        {
                            productGroup: { include: true },
                            recommendationLists,
                        },
                        options
                    );
                } catch (error) {
                    lastError = error;
                    window.VOYADO_ELEVATE?.debug?.('Recommendation section product key did not resolve', {
                        sectionId: this.config.sectionId,
                        productKey,
                        status: error?.status ?? null,
                    });
                }
            }

            throw lastError;
        }

        matchingList(response) {
            const lists = Array.isArray(response?.recommendationLists)
                ? response.recommendationLists
                : [];
            const configuredId = String(this.config.recommendationList?.id || '');

            return lists.find((list) => String(list?.id || '') === configuredId);
        }

        hideEmpty() {
            this.querySelector('[data-voyado-loading]')?.remove();
            this.removeAttribute('aria-busy');
            this.hidden = true;
        }

        async render(list) {
            const products = (list?.productGroups || [])
                .map((group) => group?.products?.[0])
                .filter(Boolean);
            const loader = window.VOYADO_PRODUCT_CARD_LOADER;
            if (!loader || !products.length || list?.visible === false) return false;

            const cards = (await Promise.all(products.map((item) => loader.load(item))))
                .filter(Boolean);
            if (!cards.length || !this.isConnected) return false;

            this.querySelector('[data-voyado-products]')?.replaceChildren(...cards);
            this.querySelector('gridy-slider')?.refresh();
            this.renderShowMore(
                list?.showMoreLink || this.config.recommendationList?.showMoreLink
            );
            this.querySelector('[data-voyado-loading]')?.remove();
            this.hidden = false;
            this.removeAttribute('aria-busy');
            return true;
        }

        renderShowMore(value) {
            const container = this.querySelector('[data-voyado-show-more]');
            if (!container || !value) {
                container?.remove();
                return;
            }

            try {
                const url = new URL(value, window.location.origin);
                if (!['http:', 'https:'].includes(url.protocol)) throw new Error();
                container.querySelector('a').href = url.toString();
                container.hidden = false;
            } catch {
                container.remove();
            }
        }

        async load() {
            if (this.requestStarted) return;

            let sections;

            try {
                sections = this.requestGroup();
                sections.forEach((section) => {
                    section.requestStarted = true;
                    section.hidden = false;
                    section.setAttribute('aria-busy', 'true');
                });

                const response = await this.request(sections);
                await Promise.all(sections.map(async (section) => {
                    if (!await section.render(section.matchingList(response))) {
                        section.hideEmpty();
                    }
                }));
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    const details = {
                        sectionId: this.config.sectionId,
                        message: error?.message || String(error),
                        status: error?.status ?? null,
                    };
                    console.warn('[Voyado recommendations] Section could not load', details);
                    window.VOYADO_ELEVATE?.debug?.('Recommendation section could not load', details);
                    (sections?.length ? sections : [this]).forEach((section) => {
                        section.hideEmpty();
                    });
                }
            } finally {
                (sections?.length ? sections : [this]).forEach((section) => {
                    section.removeAttribute('aria-busy');
                });
            }
        }
    }

    customElements.define('voyado-recommendations-section', VoyadoRecommendationsSection);
})(window, document);
